import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import Select from 'react-select';
import { ToastContainer, toast, ToastOptions } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, TextField, Label,
} from '@equinor/eds-core-react';
import {
  UpdateTopic, DeleteTopic, CreateTopic, CreateSynonym, CreateAntonym,
} from '../store/topics/actions';
import { Topic } from '../store/topics/types';
import RootState from '../store';

import 'react-confirm-alert/src/react-confirm-alert.css';
import 'react-toastify/dist/ReactToastify.css';
import './TopicDetail.css';
import { CustomConfirm } from './CustomConfirm';
import {
  TOPIC_INVALID_CHARACTER_ERROR_MSG,
  TOPIC_MAXLENGTH_COMMENTS,
  TOPIC_MAXLENGTH_DEFINITION,
  TOPIC_MAXLENGTH_TERM,
  TOPIC_MAXLENGTH_TOPIC_NAME,
  TOPIC_MAXLENGTH_WARNING_MSG,
  TOPIC_UNIQUE_VALUE_ERROR_MSG,
} from '../constants';

export function TopicDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [topic, setTopic] = useState({ ...location.state.topic });
  const [isChange, setIsChange] = useState(false);
  const [disable, setDisable] = useState(false);
  const [isValid, setIsValid] = useState(location.state.activity === 'edit');
  const [errMsg, setErrMsg] = useState('');

  const [newSynonym, setNewSynonym] = useState('');
  const [newAntonym, setNewAntonym] = useState('');

  const [synonyms, setSynonyms] = useState(location.state.state.synonyms.map((x: string) => ({ value: x, label: x })));
  const [antonyms, setAntonyms] = useState(location.state.state.antonyms.map((x: string) => ({ value: x, label: x })));

  const state = useSelector((state: RootState) => state);
  const { topicState, synonymCreateState, antonymCreateState } = state;

  useEffect(() => {
    const toastOptions: ToastOptions<{}> = {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    };

    const toastSucceeded = (msg: string) => toast.info(msg, toastOptions);
    const toastFailed = (msg: string) => toast.error(msg, toastOptions);
    const toastInfo = (msg: string) => toast.success(msg, toastOptions);

    const setEditProps = () => {
      setIsChange(false);
      location.state.topic = topic;
      location.state.activity = 'edit';
    };

    if (topicState.isSaveSuccess === true) {
      topicState.isSaveSuccess = null;

      topic.topicId = topicState.createdTopicId ?? topic.topicId;

      setEditProps();
      toastSucceeded('Saved');
      setDisable(false);
    }

    if (topicState.isSaveSuccess === false) {
      topicState.isSaveSuccess = null;

      topic.isActive = location.state.topic.isActive;

      toastFailed('Save failed');
      setDisable(false);
    }

    if (topicState.isDeleteSuccess === true) {
      topicState.isDeleteSuccess = null;

      toastSucceeded('Topic deleted');

      setTimeout(() => {
        void navigate(-1);
      }, 2000);
    }

    if (topicState.isDeleteSuccess === false) {
      topicState.isDeleteSuccess = null;

      toastFailed('Delete failed');
      setDisable(false);
    }

    if (synonymCreateState.isCreateSuccess === true) {
      synonymCreateState.isCreateSuccess = null;

      const synonym = synonymCreateState.term;
      addSynonymToTopic(synonym);

      const synonymsMod = synonyms.slice();
      synonymsMod.push({ value: synonym, label: synonym });
      setSynonyms(synonymsMod);

      toastInfo('Synonym term added');
      setNewSynonym('');
      setDisable(false);
    }

    if (synonymCreateState.isCreateSuccess === false) {
      synonymCreateState.isCreateSuccess = null;

      toastFailed('Synonym term failed');
      setDisable(false);
    }

    if (antonymCreateState.isCreateSuccess === true) {
      antonymCreateState.isCreateSuccess = null;

      const antonym = antonymCreateState.term;
      addAntonymToTopic(antonym);

      const antonymsMod = antonyms.slice();
      antonymsMod.push({ value: antonym, label: antonym });
      setAntonyms(antonymsMod);

      toastInfo('Antonym term added');
      setNewAntonym('');
      setDisable(false);
    }

    if (antonymCreateState.isCreateSuccess === false) {
      antonymCreateState.isCreateSuccess = null;

      toastFailed('Antonym term failed');
      setDisable(false);
    }
  }, [topicState, location.state, navigate, synonymCreateState]);

  const dropDownStyle = {
    control: (base: any, _state: any) => ({
      ...base,
      width: '900px',
      border: 'none',
      borderRadius: '0',
      background: '#F7F7F7',
      borderBottom: '1px solid #999',
      boxShadow: 'none',
    }),
  };

  const onChange = (event: any) => {
    const modifiedTopic: Topic = { ...topic };
    const { value } = event.target;

    switch (event.target.id) {
      case 'name':
        modifiedTopic.name = value;
        setIsValid(validateTopicName(value));
        break;

      case 'definition':
        modifiedTopic.definition = value;
        break;

      case 'comments':
        modifiedTopic.comments = value;
        break;

      case 'experienceScore':
        modifiedTopic.experienceScore = value;
        break;

      case 'incidentScore':
        modifiedTopic.incidentScore = value;
        break;

      case 'operationScore':
        modifiedTopic.operationScore = value;
        break;

      case 'operationSummaryScore':
        modifiedTopic.operationSummaryScore = value;
        break;

      case 'sectionSummaryScore':
        modifiedTopic.sectionSummaryScore = value;
        break;

      case 'dailyReportScore':
        modifiedTopic.dailyReportScore = value;
        break;

      default:
        break;
    }

    setIsChange(true);
    setTopic(modifiedTopic);
  };

  const onChangeSynonyms = (data:any) => {
    const selectedSynonyms = data.map((x: { value: string; }) => x.value);
    const modifiedTopic:Topic = { ...topic };
    modifiedTopic.synonyms = selectedSynonyms;

    setIsChange(true);
    setTopic(modifiedTopic);
  };

  const onChangeAntonyms = (data:any) => {
    const selectedAntonyms = data.map((x: { value: string; }) => x.value);
    const modifiedTopic:Topic = { ...topic };
    modifiedTopic.antonyms = selectedAntonyms;

    setIsChange(true);
    setTopic(modifiedTopic);
  };

  const reset = () => {
    setIsChange(false);
    setTopic({ ...location.state.topic });
    setIsValid(location.state.activity === 'edit');
  };

  const abort = () => {
    if (isChange) {
      CustomConfirm(
        'Close and discard changes?',
        'All potential changes will be lost.',
        () => { void navigate(-1); },
        () => {},
      );
    } else {
      void navigate(-1);
    }
  };

  const deleteTopic = () => {
    setDisable(true);

    CustomConfirm(
      'Delete topic?',
      'All topic data will be lost.',
      () => { dispatch(DeleteTopic(topic)); },
      () => { setDisable(false); },
    );
  };

  const toggleActive = () => {
    if (isChange) {
      CustomConfirm(
        'Discard changes?',
        'All potential changes will be lost.',
        () => { execToggleActive(); },
        () => {},
      );
    } else {
      execToggleActive();
    }
  };

  const execToggleActive = () => {
    setDisable(true);

    const modifiedTopic: Topic = { ...location.state.topic };
    modifiedTopic.isActive = !modifiedTopic.isActive;

    setTopic(modifiedTopic);
    dispatch(UpdateTopic(modifiedTopic));
  };

  const saveTopic = () => {
    setDisable(true);

    if (location.state.activity === 'edit') {
      dispatch(UpdateTopic(topic));
    } else {
      dispatch(CreateTopic(topic));
    }
  };

  const validateTopicName = (name: string) : boolean => {
    const validChars = /^[A-Za-z]+[A-Za-z/() -]+[A-Za-z)]+$/;
    if (!name.match(validChars)) {
      setErrMsg(TOPIC_INVALID_CHARACTER_ERROR_MSG);
      return false;
    }

    if (topicState.topics.some((x) => x.topicId !== topic.topicId && x.name.toLowerCase() === name.trim().toLowerCase())) {
      setErrMsg(TOPIC_UNIQUE_VALUE_ERROR_MSG);
      return false;
    }

    setErrMsg('');
    return true;
  };

  const addSynonymKeyPress = (event:any) => {
    if (event.key === 'Enter') {
      addSynonym();
    }
  };

  const addSynonym = () => {
    setDisable(true);

    let isAdded = false;

    const synonym = newSynonym.trim();
    if (!synonym) {
      setDisable(false);
      return;
    }

    var isExistInTopic = topic.synonyms.includes(synonym);

    var isExistInSynonyms = synonyms.some((s:any) => s.value === synonym);

    if (isExistInTopic) {
      isAdded = true;
    } else if (isExistInSynonyms) {
      addSynonymToTopic(synonym);
      isAdded = true;
    } else {
      dispatch(CreateSynonym(synonym));
    }

    if (isAdded) {
      setNewSynonym('');
      setDisable(false);
    }
  };

  const addSynonymToTopic = (synonym: string) => {
    const modifiedTopic: Topic = { ...topic };
    modifiedTopic.synonyms = modifiedTopic.synonyms.slice();
    modifiedTopic.synonyms.push(synonym);
    setTopic(modifiedTopic);
    setIsChange(true);
  };

  const addAntonymKeyPress = (event:any) => {
    if (event.key === 'Enter') {
      addAntonym();
    }
  };

  const addAntonym = () => {
    setDisable(true);

    let isAdded = false;

    const antonym = newAntonym.trim();
    if (!antonym) {
      setDisable(false);
      return;
    }

    var isExistInTopic = topic.antonyms.includes(antonym);

    var isExistInAntonyms = antonyms.some((s:any) => s.value === antonym);

    if (isExistInTopic) {
      isAdded = true;
    } else if (isExistInAntonyms) {
      addAntonymToTopic(antonym);
      isAdded = true;
    } else {
      dispatch(CreateAntonym(antonym));
    }

    if (isAdded) {
      setNewAntonym('');
      setDisable(false);
    }
  };

  const addAntonymToTopic = (antonym: string) => {
    const modifiedTopic: Topic = { ...topic };
    modifiedTopic.antonyms = modifiedTopic.antonyms.slice();
    modifiedTopic.antonyms.push(antonym);
    setTopic(modifiedTopic);
    setIsChange(true);
  };

  return (
    <div className="topic-form-wrapper">
      <div className="topic-form-button-wrapper">
        <Button
          disabled={disable || !isValid}
          variant="outlined"
          onClick={saveTopic}
        >
          Save
        </Button>

        <Button
          disabled={disable || location.state.activity !== 'edit'}
          variant="outlined"
          onClick={deleteTopic}
        >
          Delete
        </Button>

        <Button
          disabled={disable || location.state.activity !== 'edit'}
          variant="outlined"
          onClick={toggleActive}
        >
          {topic.isActive ? 'Deactivate' : 'Activate'}
        </Button>

        <Button
          disabled={disable}
          variant="outlined"
          onClick={reset}
        >
          Reset
        </Button>

        <Button
          disabled={disable}
          variant="outlined"
          onClick={abort}
        >
          Close
        </Button>
      </div>
      <form>
        <div className="topic-form-textfield-wrapper">
          <TextField
            disabled={disable}
            id="name"
            label="Topic name *"
            value={topic.name}
            onChange={onChange}
            required
            maxLength={TOPIC_MAXLENGTH_TOPIC_NAME}
            helperText={isValid ? topic.name.length >= TOPIC_MAXLENGTH_TOPIC_NAME ? `${TOPIC_MAXLENGTH_WARNING_MSG} (${TOPIC_MAXLENGTH_TOPIC_NAME})` : undefined : errMsg}
            variant={isValid ? topic.name.length >= TOPIC_MAXLENGTH_TOPIC_NAME ? 'warning' : undefined : 'error'}
          />
        </div>
        <div className="topic-form-textfield-wrapper">
          <TextField
            disabled={disable}
            id="definition"
            label="Definition"
            multiline
            rowsMax={5}
            value={topic.definition}
            onChange={onChange}
            maxLength={TOPIC_MAXLENGTH_DEFINITION}
            helperText={topic.definition.length >= TOPIC_MAXLENGTH_DEFINITION ? `${TOPIC_MAXLENGTH_WARNING_MSG} (${TOPIC_MAXLENGTH_DEFINITION})` : undefined}
            variant={topic.definition.length >= TOPIC_MAXLENGTH_DEFINITION ? 'warning' : undefined}
          />
        </div>
        <div className="topic-form-textfield-wrapper">
          <TextField
            disabled={disable}
            id="comments"
            label="Comments"
            multiline
            rowsMax={5}
            value={topic.comments}
            onChange={onChange}
            maxLength={TOPIC_MAXLENGTH_COMMENTS}
            helperText={topic.comments.length >= TOPIC_MAXLENGTH_COMMENTS ? `${TOPIC_MAXLENGTH_WARNING_MSG} (${TOPIC_MAXLENGTH_COMMENTS})` : undefined}
            variant={topic.comments.length >= TOPIC_MAXLENGTH_COMMENTS ? 'warning' : undefined}
          />
        </div>
        <div className="topic-form-score-wrapper">
          <TextField
            disabled={disable}
            id="experienceScore"
            label="Experience score"
            type="number"
            value={topic.experienceScore}
            onChange={onChange}
          />
          <TextField
            disabled={disable}
            id="incidentScore"
            label="Incident score"
            type="number"
            value={topic.incidentScore}
            onChange={onChange}
          />
          <TextField
            disabled={disable}
            id="operationScore"
            label="Operation score"
            type="number"
            value={topic.operationScore}
            onChange={onChange}
          />
          <TextField
            disabled={disable}
            id="operationSummaryScore"
            label="Operation summary score"
            type="number"
            value={topic.operationSummaryScore}
            onChange={onChange}
          />
          <TextField
            disabled={disable}
            id="sectionSummaryScore"
            label="Section summary score"
            type="number"
            value={topic.sectionSummaryScore}
            onChange={onChange}
          />
          <TextField
            disabled={disable}
            id="dailyReportScore"
            label="Daily report score"
            type="number"
            value={topic.dailyReportScore}
            onChange={onChange}
          />

        </div>
        <Label className="topic-form-term-label" label="Synonyms terms" />
        <div>
          <Select
            isDisabled={disable}
            styles={dropDownStyle}
            id="synonyms"
            placeholder="Select synonyms terms..."
            options={synonyms}
            value={topic.synonyms.map((x: string) => ({ value: x, label: x }))}
            onChange={onChangeSynonyms}
            isMulti
          />
          <div className="new-term-wrapper">
            <div className="new-term-text-wrapper">
              <TextField
                disabled={disable}
                id="newSynonymTextField"
                label="New synonym term"
                value={newSynonym}
                onChange={(event:any) => setNewSynonym(event.target.value)}
                onKeyPress={addSynonymKeyPress}
                maxLength={TOPIC_MAXLENGTH_TERM}
                variant={newSynonym.length >= TOPIC_MAXLENGTH_TERM ? 'warning' : undefined}
                helperText={newSynonym.length >= TOPIC_MAXLENGTH_TERM ? `${TOPIC_MAXLENGTH_WARNING_MSG} (${TOPIC_MAXLENGTH_TERM})` : undefined}
              />
            </div>
            <div className="new-term-btn-wrapper">
              <Button
                disabled={disable}
                variant="ghost"
                onClick={addSynonym}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <Label className="topic-form-term-label" label="Antonyms terms" />
        <div>
          <Select
            isDisabled={disable}
            styles={dropDownStyle}
            id="antonyms"
            placeholder="Select antonyms terms..."
            options={antonyms}
            value={topic.antonyms.map((x: string) => ({ value: x, label: x }))}
            onChange={onChangeAntonyms}
            isMulti
          />
          <div className="new-term-wrapper">
            <div className="new-term-text-wrapper">
              <TextField
                disabled={disable}
                id="newAntonymTextField"
                label="New antonym term"
                value={newAntonym}
                onChange={(event:any) => setNewAntonym(event.target.value)}
                onKeyPress={addAntonymKeyPress}
                maxLength={TOPIC_MAXLENGTH_TERM}
                variant={newAntonym.length >= TOPIC_MAXLENGTH_TERM ? 'warning' : undefined}
                helperText={newAntonym.length >= TOPIC_MAXLENGTH_TERM ? `${TOPIC_MAXLENGTH_WARNING_MSG} (${TOPIC_MAXLENGTH_TERM})` : undefined}
              />
            </div>
            <div className="new-term-btn-wrapper">
              <Button
                disabled={disable}
                variant="ghost"
                onClick={addAntonym}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </form>
      <ToastContainer />
    </div>
  );
}
